import React, { useState } from 'react';
import { Card, Typography, Form, Input, Button, Alert, Result } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, FileTextOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import toast from 'react-hot-toast';

const { Title, Text } = Typography;
const { TextArea } = Input;

const RequestAccount: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (values: any) => {
    setLoading(true);
    setError('');

    try {
      await api.post('/api/auth/request-account', {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone,
        notes: values.notes,
      });

      setSuccess(true);
      toast.success('Account request submitted successfully!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to submit account request';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          padding: 24,
        }}
      >
        <Card style={{ width: '100%', maxWidth: 500, textAlign: 'center' }}>
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="Request Submitted Successfully!"
            subTitle="Your account request has been submitted. You will receive an email once your request is approved by an administrator."
            extra={
              <Button type="primary" size="large" onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: 24,
      }}
    >
      <Card style={{ width: '100%', maxWidth: 500 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
          <UserOutlined style={{ fontSize: 24, marginRight: 12, color: '#1976d2' }} />
          <Title level={3} style={{ margin: 0 }}>
            Request New Account
          </Title>
        </div>

        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          Fill out the form below to request a new account. An administrator will review your request and send you an email once approved.
        </Text>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError('')}
            style={{ marginBottom: 24 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          size="large"
        >
          <Form.Item
            name="first_name"
            label="First Name"
            rules={[
              { required: true, message: 'Please enter your first name' },
              { min: 2, message: 'First name must be at least 2 characters' },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Enter your first name"
            />
          </Form.Item>

          <Form.Item
            name="last_name"
            label="Last Name"
            rules={[
              { required: true, message: 'Please enter your last name' },
              { min: 2, message: 'Last name must be at least 2 characters' },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Enter your last name"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please enter your email address' },
              { type: 'email', message: 'Please enter a valid email address' },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="Enter your email address"
              type="email"
            />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[
              { required: true, message: 'Please enter your phone number' },
            ]}
          >
            <Input
              prefix={<PhoneOutlined />}
              placeholder="Enter your phone number"
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes (Optional)"
            help="Any additional information you'd like to provide"
          >
            <TextArea
              placeholder="Enter any additional notes or information"
              rows={4}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </Button>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="link"
              onClick={() => navigate('/login')}
              disabled={loading}
              block
            >
              Already have an account? Login here
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default RequestAccount;

